/**
 * Caixa de escrever da conversa: manda pelo WhatsApp da equipe SEM marcar a
 * conversa como lida (marcar é escolha explícita, desligada por padrão).
 *
 * - Texto que cresce; Enter manda, Shift+Enter quebra a linha (no celular,
 *   Enter quebra a linha e quem manda é o botão, como no WhatsApp).
 * - Um anexo por vez (foto, vídeo, PDF, .skp, qualquer documento, até 16 MB),
 *   visto antes de mandar, com legenda opcional. Colar um arquivo também anexa.
 * - Áudio gravado no navegador: cronômetro, cancelar, ouvir antes de mandar.
 * - Responder citando: a citação aparece em cima da caixa.
 * - O texto escrito fica guardado por conversa: sair e voltar não o perde.
 *
 * Quem cuida do envio (balão "enviando…", erro, tentar de novo) é a lista de
 * comandos; aqui a caixa só monta o pedido e se limpa.
 */
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { FiFileText, FiMic, FiPaperclip, FiSend, FiSquare, FiTrash2, FiVideo, FiX } from "react-icons/fi";
import styled, { css, keyframes } from "styled-components";

import {
  TAMANHO_MAXIMO_DO_ENVIO,
  TAMANHO_MAXIMO_DO_TEXTO,
  autorDaMensagem,
  duracaoDoAudio,
  tamanhoLegivel,
  tipoDoAnexo,
  trechoDaMensagem,
} from "../apresentacao";
import { Citacao } from "../Mensagens";
import { guardarRascunho, lerRascunho } from "./rascunhos";
import useGravadorDeAudio from "./useGravadorDeAudio";

const ALTURA_MAXIMA_DO_TEXTO = 160;

const Caixa = styled.footer.attrs({ className: "cp-envio" })`
  flex: 0 0 auto;
  display: grid;
  gap: 8px;
  padding: 10px 12px calc(8px + var(--safe-area-bottom, 0px));
  border-top: 1px solid var(--border);
  background: var(--surface);
`;

/* Citação e anexo: faixas acima da linha de escrever. */
const Faixa = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 6px 6px 6px 8px;
  border-radius: 12px;
  background: var(--surface-2);

  > div:first-child {
    flex: 1 1 auto;
    min-width: 0;
  }

  ${Citacao} {
    margin: 0;
    background: transparent;
  }
`;

const Miniatura = styled.span`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  overflow: hidden;
  border-radius: 9px;
  background: var(--surface);
  color: var(--ink-2);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NomeDoAnexo = styled.div`
  display: grid;
  gap: 1px;
  min-width: 0;

  b {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  small {
    font-size: 12px;
    color: var(--ink-3);
  }
`;

const Linha = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  min-width: 0;
`;

const redondo = css`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  font: inherit;
  cursor: pointer;
  transition: background-color 120ms ease-out, transform 120ms ease-out;

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:active:not(:disabled) {
      transform: none;
    }
  }
`;

const BotaoDeFerramenta = styled.button`
  ${redondo}
  background: transparent;
  color: var(--ink-2);

  &:hover:not(:disabled) {
    background: var(--surface-2);
    color: var(--ink);
  }
`;

/** A ação principal da caixa: mandar (ou parar a gravação). */
const BotaoPrincipal = styled.button`
  ${redondo}
  background: var(--accent);
  color: var(--ink-on-accent);

  &:hover:not(:disabled) {
    background: var(--accent-hover);
  }
`;

const BotaoFechar = styled.button`
  ${redondo}
  width: 30px;
  height: 30px;
  background: transparent;
  color: var(--ink-3);

  &:hover {
    background: var(--surface);
    color: var(--ink);
  }
`;

const Campo = styled.textarea`
  flex: 1 1 auto;
  box-sizing: border-box;
  min-width: 0;
  min-height: 40px;
  max-height: ${ALTURA_MAXIMA_DO_TEXTO}px;
  padding: 9px 14px;
  border: 1px solid transparent;
  border-radius: 20px;
  background: var(--surface-2);
  color: var(--ink);
  font: inherit;
  font-size: 14.5px;
  line-height: 1.45;
  resize: none;
  overflow-y: auto;

  &::placeholder {
    color: var(--ink-3);
  }

  &:focus {
    outline: none;
    border-color: var(--border-strong);
    background: var(--surface);
  }
`;

const piscar = keyframes`
  from { opacity: 1; }
  to { opacity: 0.25; }
`;

const Gravando = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
  height: 40px;
  padding: 0 14px;
  border-radius: 20px;
  background: var(--surface-2);
  font-size: 14px;
  color: var(--ink-2);

  b {
    font-weight: 600;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }

  audio {
    flex: 1 1 auto;
    min-width: 0;
    height: 32px;
  }
`;

/* O ponto vermelho é estado (o microfone está aberto), não enfeite. */
const PontoGravando = styled.span`
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--danger-solid);
  animation: ${piscar} 800ms ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Pe = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 12px;
  padding: 0 4px 0 6px;
  font-size: 12px;
  line-height: 1.35;
  color: var(--ink-3);

  label {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 28px;
    cursor: pointer;
    color: var(--ink-2);
  }

  input {
    width: 15px;
    height: 15px;
    margin: 0;
    accent-color: var(--accent);
  }

  > span {
    margin-left: auto;
    text-align: right;
  }
`;

const Erro = styled.p`
  margin: 0;
  padding: 0 6px;
  font-size: 12.5px;
  color: var(--danger-text);
`;

/** No celular o Enter quebra a linha (o teclado não tem Shift à mão). */
function enterQuebraLinha() {
  try {
    return Boolean(window.matchMedia?.("(pointer: coarse)")?.matches);
  } catch {
    return false;
  }
}

function PreviaDoAnexo({ anexo, aoTirar }) {
  const tipo = tipoDoAnexo(anexo.arquivo);
  let miniatura;
  if (tipo === "imagem" && anexo.url) miniatura = <img src={anexo.url} alt="" />;
  else if (tipo === "video") miniatura = <FiVideo size={20} aria-hidden="true" />;
  else miniatura = <FiFileText size={20} aria-hidden="true" />;
  return (
    <Faixa aria-label="Anexo a enviar">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Miniatura>{miniatura}</Miniatura>
        <NomeDoAnexo>
          <b title={anexo.arquivo.name}>{anexo.arquivo.name}</b>
          <small>{tamanhoLegivel(anexo.arquivo.size)} · a legenda é opcional</small>
        </NomeDoAnexo>
      </div>
      <BotaoFechar type="button" aria-label={`Tirar o anexo ${anexo.arquivo.name}`} title="Tirar o anexo" onClick={aoTirar}>
        <FiX size={16} aria-hidden="true" />
      </BotaoFechar>
    </Faixa>
  );
}

/** Citação em cima da caixa. `titulo` troca o "Respondendo a <autor>" (o Assistente usa). */
export function FaixaDaCitacao({ mensagem, aoCancelar, titulo = "" }) {
  return (
    <Faixa aria-label="Respondendo a mensagem">
      <div>
        <Citacao>
          <b>{titulo || `Respondendo a ${autorDaMensagem(mensagem)}`}</b>
          <span>{trechoDaMensagem(mensagem)}</span>
        </Citacao>
      </div>
      <BotaoFechar type="button" aria-label="Cancelar a resposta" title="Cancelar a resposta" onClick={aoCancelar}>
        <FiX size={16} aria-hidden="true" />
      </BotaoFechar>
    </Faixa>
  );
}

export default function CaixaDeEnvio({ conversa, citando, aoCancelarCitacao, aoEnviar, aoMudarAudio }) {
  const [texto, setTexto] = useState(() => lerRascunho(conversa.id));
  const [anexo, setAnexo] = useState(null);
  const [marcarLida, setMarcarLida] = useState(false);
  const [erro, setErro] = useState("");
  const campo = useRef(null);
  const seletor = useRef(null);
  const audio = useGravadorDeAudio();
  const idDoErro = useId();

  useEffect(() => {
    if (citando) campo.current?.focus();
  }, [citando]);

  // Quem fecha a conversa (ESC) precisa saber se há áudio para não perder.
  useEffect(() => {
    aoMudarAudio?.(audio.fase !== "parado");
  }, [audio.fase, aoMudarAudio]);

  // Rascunho que voltou de outra visita: a caixa já abre na altura do texto.
  useLayoutEffect(() => {
    if (texto) ajustarAltura();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A miniatura do anexo é um endereço local: solta ao trocar ou sair.
  useEffect(() => () => {
    if (anexo?.url) URL.revokeObjectURL(anexo.url);
  }, [anexo]);

  function ajustarAltura() {
    const el = campo.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, ALTURA_MAXIMA_DO_TEXTO)}px`;
  }

  function escolherArquivo(arquivo) {
    if (!arquivo) return;
    if (arquivo.size > TAMANHO_MAXIMO_DO_ENVIO) {
      setErro(`${arquivo.name} tem ${tamanhoLegivel(arquivo.size)}; o limite para mandar pela Central é 16 MB.`);
      return;
    }
    setErro("");
    const url = tipoDoAnexo(arquivo) === "imagem" && typeof URL.createObjectURL === "function"
      ? URL.createObjectURL(arquivo)
      : "";
    setAnexo({ arquivo, url });
    campo.current?.focus();
  }

  function escrever(valor) {
    setTexto(valor);
    guardarRascunho(conversa.id, valor);
  }

  function limpar() {
    escrever("");
    setAnexo(null);
    setMarcarLida(false);
    setErro("");
    aoCancelarCitacao?.();
    window.requestAnimationFrame?.(ajustarAltura);
  }

  function enviar() {
    const limpo = texto.trim();
    const base = { citada: citando || null, marcarLida };
    if (audio.fase === "pronto" && audio.gravacao) {
      aoEnviar({ ...base, tipo: "audio", arquivo: audio.gravacao.arquivo, duracao: audio.gravacao.duracao });
      audio.entregar();
      limpar();
      return;
    }
    if (anexo) aoEnviar({ ...base, tipo: "arquivo", arquivo: anexo.arquivo, texto: limpo });
    else if (limpo) aoEnviar({ ...base, tipo: "texto", texto: limpo });
    else return;
    limpar();
    campo.current?.focus();
  }

  function aoTeclar(evento) {
    if (evento.key === "Escape" && citando) {
      evento.preventDefault();
      aoCancelarCitacao();
      return;
    }
    if (evento.key !== "Enter" || evento.shiftKey || evento.nativeEvent?.isComposing || enterQuebraLinha()) return;
    evento.preventDefault();
    enviar();
  }

  const temConteudo = Boolean(texto.trim() || anexo);
  const erroVisivel = erro || audio.erro;
  let linha;
  if (audio.fase === "gravando" || audio.fase === "pedindo") {
    linha = (
      <Linha>
        <BotaoDeFerramenta type="button" aria-label="Cancelar a gravação" title="Cancelar" onClick={audio.descartar}>
          <FiTrash2 size={19} aria-hidden="true" />
        </BotaoDeFerramenta>
        <Gravando role="status" aria-live="polite">
          {audio.fase === "pedindo" ? (
            "Esperando o microfone…"
          ) : (
            <>
              <PontoGravando aria-hidden="true" />
              <b>{duracaoDoAudio(audio.segundos)}</b>
              Gravando
            </>
          )}
        </Gravando>
        <BotaoPrincipal
          type="button"
          aria-label="Parar a gravação"
          title="Parar e ouvir antes de mandar"
          disabled={audio.fase !== "gravando"}
          onClick={audio.parar}
        >
          <FiSquare size={16} aria-hidden="true" />
        </BotaoPrincipal>
      </Linha>
    );
  } else if (audio.fase === "pronto" && audio.gravacao) {
    linha = (
      <Linha>
        <BotaoDeFerramenta type="button" aria-label="Descartar o áudio" title="Descartar" onClick={audio.descartar}>
          <FiTrash2 size={19} aria-hidden="true" />
        </BotaoDeFerramenta>
        <Gravando>
          <audio src={audio.gravacao.url} controls preload="metadata" aria-label="Ouvir o áudio gravado" />
          <b>{duracaoDoAudio(audio.gravacao.duracao)}</b>
        </Gravando>
        <BotaoPrincipal type="button" aria-label="Mandar o áudio" title="Mandar o áudio" onClick={enviar}>
          <FiSend size={17} aria-hidden="true" />
        </BotaoPrincipal>
      </Linha>
    );
  } else {
    linha = (
      <Linha>
        <input
          ref={seletor}
          type="file"
          hidden
          data-testid="anexo-da-conversa"
          onChange={(evento) => {
            escolherArquivo(evento.target.files?.[0]);
            evento.target.value = "";
          }}
        />
        <BotaoDeFerramenta
          type="button"
          aria-label="Anexar arquivo"
          title="Anexar foto, vídeo ou documento (até 16 MB)"
          onClick={() => seletor.current?.click()}
        >
          <FiPaperclip size={19} aria-hidden="true" />
        </BotaoDeFerramenta>
        <Campo
          ref={campo}
          rows={1}
          value={texto}
          maxLength={TAMANHO_MAXIMO_DO_TEXTO}
          placeholder={anexo ? "Legenda (opcional)" : "Escreva uma mensagem"}
          aria-label={anexo ? "Legenda do anexo" : `Mensagem para ${conversa.nome}`}
          aria-describedby={erroVisivel ? idDoErro : undefined}
          onChange={(evento) => {
            escrever(evento.target.value);
            ajustarAltura();
          }}
          onKeyDown={aoTeclar}
          onPaste={(evento) => {
            const arquivo = evento.clipboardData?.files?.[0];
            if (!arquivo) return;
            evento.preventDefault();
            escolherArquivo(arquivo);
          }}
        />
        {temConteudo ? (
          <BotaoPrincipal type="button" aria-label="Enviar" title="Enviar (Enter)" onClick={enviar}>
            <FiSend size={17} aria-hidden="true" />
          </BotaoPrincipal>
        ) : (
          <BotaoDeFerramenta
            type="button"
            aria-label="Gravar áudio"
            title="Gravar áudio"
            onClick={() => {
              setErro("");
              audio.iniciar();
            }}
          >
            <FiMic size={20} aria-hidden="true" />
          </BotaoDeFerramenta>
        )}
      </Linha>
    );
  }

  return (
    <Caixa aria-label="Responder pelo WhatsApp">
      {citando ? <FaixaDaCitacao mensagem={citando} aoCancelar={aoCancelarCitacao} /> : null}
      {anexo && audio.fase === "parado" ? <PreviaDoAnexo anexo={anexo} aoTirar={() => setAnexo(null)} /> : null}
      {erroVisivel ? (
        <Erro id={idDoErro} role="alert">
          {erroVisivel}
        </Erro>
      ) : null}
      {linha}
      <Pe>
        <label>
          <input type="checkbox" checked={marcarLida} onChange={(evento) => setMarcarLida(evento.target.checked)} />
          <span>
            Marcar como lida<span className="cp-so-largo"> ao enviar</span>
          </span>
        </label>
        {marcarLida ? (
          <span>Envia e marca a conversa como lida</span>
        ) : (
          <span>
            <span className="cp-so-largo">Envia pelo WhatsApp da equipe sem marcar como lida</span>
            <span className="cp-so-estreito">Envia sem marcar como lida</span>
          </span>
        )}
      </Pe>
    </Caixa>
  );
}
